<template>
  <v-row wrap
    class="px-4 pt-0 ma-0 sparkline-outer-container"
    :style="containerStyle"
    no-gutters
  >
    <div v-if="!requiredFiltersAreSet">
      Select filter: {{ requiredFilterField }}
    </div>
    <div v-else class="sparkline-container">
      <div class="sparkline-max-value">{{ maxValue }}</div>
      <div class="sparkline-title">
        <b>{{ moduleView.name }}</b> {{ firstLabel }} - {{ lastLabel }}
      </div>
      <v-sparkline
        v-if="show"
        :value="values"
        :gradient="gradient"
        :smooth="sparklineRadius || false"
        :padding="sparklinePadding"
        :line-width="sparklineLineWidth"
        :stroke-linecap="lineCap"
        :gradient-direction="sparklineGradientDirection"
        :fill="sparklineFill"
        :type="sparklineType"
        :auto-line-width="autoLineWidth"
        auto-draw
        :auto-draw-duration="300"
        :labels="spaceForLabel > 30 ? labels : []"
        :width="$store.state.innerWidth"
        :height="300"
        :label-size="labelSize"
        :show-labels="spaceForLabel > 30 ? false : false"
      />
      <div class="sparkline-min-value">{{ minValue }}</div>
    </div>
  </v-row>
</template>

<script>
import moment from 'moment'

const moduleViewConfFields = ['sumField',
  'maxField',
  'valueField',
  'value2Field',
  'dateField',
  'dateEndField',
  'requiredFilterField',
  'fillMissingPeriodValuesAsZero',
  'fillOnlyOneEmptyZeroValue',
  'periodInterval',

  'sparklineLineWidth',
  // 'sparklineRadius', - no need
  // 'sparklinePadding', - no need
  'sparklineGradientDirection',
  'sparklineGradientColors',
  'sparklineFill',
  'sparklineType',

  'countField']

export default {
  name: 'Sparkline',

  data () {
    return {
      show: true,
      sparklineLineWidth: 5,
      sparklineRadius: 20,
      sparklinePadding: 8,
      lineCap: 'round',
      sparklineGradientColors: '#f72047,#ffd200,#1feaea',
      sparklineGradientDirection: 'top',
      sparklineFill: false,
      sparklineType: 'trend',
      autoLineWidth: false,
      labelSize: 14,

      sumField: '',
      maxField: '',
      valueField: '',
      value2Field: '',
      dateField: '',
      dateEndField: '',
      countField: '',
      requiredFilterField: '',
      fillMissingPeriodValuesAsZero: false,
      fillOnlyOneEmptyZeroValue: true,
      periodInterval: ''
    }
  },

  props: {
    moduleName: {
      type: String,
      default: null
    },

    height: {
      type: Number,
      default: null
    },

    items: {
      type: Array,
      default: () => {
        return []
      }
    },

    fields: {
      type: Array,
      default: () => {
        return []
      }
    },

    moduleView: {
      type: Object,
      default: () => {
        return {}
      }
    },

    searchFilters: {
      type: Object,
      default: () => {
        return {}
      }
    },

    topOffset: {
      type: Number,
      default: 0
    }
  },

  created () {
    moduleViewConfFields.forEach(field => {
      if (this.moduleView[field]) {
        this[field] = this.moduleView[field]
      }
    })
    if (!this.periodInterval) { this.periodInterval = 'day' }
  },

  watch: {
    itemsLength () {
      // To trigger redraw
      this.show = false
      this.$nextTick(() => {
        this.show = true
      })
    }
  },

  computed: {
    gradient () {
      return this.sparklineGradientColors.split(',')
    },

    containerStyle () {
      return {
        height: this.height + 'px'
      }
    },

    periodIntervalFormat () {
      let format = 'YYYY-MM-DD'
      if (this.periodInterval === 'hour') { format = format = 'YYYY-MM-DD HH' }
      return format
    },

    firstLabel () {
      if (!this.allPeriodIntervals) { return }
      return moment(this.allPeriodIntervals[0]).format('DD.MM')
    },

    lastLabel () {
      if (!this.allPeriodIntervals) { return }
      return moment(this.allPeriodIntervals[this.allPeriodIntervals.length - 1]).format('DD.MM.YYYY')
    },

    values () {
      return this.allPeriodIntervals.map(periodInterval => this.itemPeriodTotals[periodInterval] || 0)
    },

    requiredFiltersAreSet () {
      return !this.requiredFilterField || this.searchFilters[this.moduleName + '_' + this.requiredFilterField]
    },

    spaceForLabel () {
      return this.$store.state.innerWidth / this.values.length
    },

    labels () {
      if (this.spaceForLabel < 10) {
        return []
      }
      const format = this.spaceForLabel < 40
        ? 'DD'
        : this.spaceForLabel < 75
          ? 'DD.MM'
          : 'DD.MM.YYYY'
      return this.allPeriodIntervals.map(periodInterval => moment(periodInterval).format(format))
    },

    itemsLength () {
      return this.items.length
    },

    itemPeriodIntervals () {
      return this.items
        .map(item => moment(item[this.dateField]).format(this.periodIntervalFormat))
        .filter((item, i, ar) => ar.indexOf(item) === i) // Get unique dates
        .sort((a, b) => moment(a) > moment(b) ? 1 : -1)
    },

    maxValue () {
      return Math.round(Math.max.apply(Math, this.values) * 100) / 100 || 0
    },

    minValue () {
      return Math.round(Math.min.apply(Math, this.values) * 100) / 100 || 0
    },

    radius2 () {
      return Math.ceil(this.maxValue / 10) || 0
    },

    itemPeriodTotals () {
      let totals = {}
      this.items.forEach(item => {
        let periodInterval = moment(item[this.dateField]).format(this.periodIntervalFormat)
        if (!totals[periodInterval]) { totals[periodInterval] = 0 }
        if (this.maxField) {
          totals[periodInterval] = totals[periodInterval] > item[this.maxField]
            ? totals[periodInterval]
            : item[this.maxField]
        } else if (this.sumField) {
          if (item[this.sumField]) {
            totals[periodInterval] += item[this.sumField]
          }
        } else if (this.valueField) {
          if (item[this.valueField]) {
            totals[periodInterval] = item[this.valueField]
          }
        } else {
          totals[periodInterval]++
        }
      })
      return totals
    },

    allPeriodIntervals () {
      if (!this.fillMissingPeriodValuesAsZero) { return this.itemPeriodIntervals }
      let periodIntervals = []
      let currentPeriodInterval = moment(this.itemPeriodIntervals[0]).subtract(1, this.periodInterval)
      const lastPeriodInterval = moment(this.itemPeriodIntervals[this.itemPeriodIntervals.length - 1]).format('YYYY-MM-DD')
      let previousPeriodInterval = null
      let periodInterval = null
      while (currentPeriodInterval <= moment(lastPeriodInterval)) {
        previousPeriodInterval = periodInterval
        periodInterval = currentPeriodInterval.format(this.periodIntervalFormat)
        if (!this.fillOnlyOneEmptyZeroValue || !previousPeriodInterval || this.itemPeriodTotals[periodInterval] || (
          this.fillOnlyOneEmptyZeroValue && this.itemPeriodIntervals.indexOf(previousPeriodInterval) !== -1
        )) {
          periodIntervals.push(periodInterval)
        }
        currentPeriodInterval = currentPeriodInterval.add(1, this.periodInterval)
      }
      return periodIntervals
    }

  },

  methods: {
  }
}
</script>

<style scoped>
.sparkline-outer-container svg {
  width: 100%;
}
.sparkline-container {
  position: relative;
  width: 100%;
}
.sparkline-max-value, .sparkline-min-value {
  position: absolute;
  left: 0;
  font-size: 12px;
  background: rgba(255,255,255,0.8);
  border-radius: 50%;
  padding: 2px;
}
.sparkline-max-value {
  top: 10px;
}
.sparkline-min-value {
  bottom: 20px;
}
.sparkline-title {
  font-size: 14px;
  padding: 2px 7px;
  position: absolute;
  top: 10px;
  text-align: center;
  left: 50%;
  transform: translate(-50%, 0);
  border: 1px solid #eee;
  background: rgba(255, 255, 255, 0.8);
  border-radius: 100px;
}
</style>
